import type { AudioType } from "@expert/shared-types";

import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

import { getIsProd } from "../../../../config";

export type { AudioType };
export type Language = "en-US" | "es-US";
export type LanguageLabel = "English" | "Spanish";
export const abbreviations = ["Eng", "Spa"] as const;
export type LanguageAbbreviation = (typeof abbreviations)[number];
export type Locale = "en-us" | "es-mx";

type LanguageStoreState = {
    label: LanguageLabel;
    language: Language;
    audioType: AudioType;
    locale: Locale;
    abbreviation: LanguageAbbreviation;
};

type LanguageStoreActions = {
    updateLanguage: (locale?: string | null) => void;
};

type LanguageStoreProps = LanguageStoreState & LanguageStoreActions;

const ENGLISH: LanguageStoreState = {
    label: "English",
    language: "en-US",
    audioType: "PreRecordedDisclosures",
    abbreviation: "Eng",
    locale: "en-us",
};

const SPANISH: LanguageStoreState = {
    label: "Spanish",
    language: "es-US",
    audioType: "PreRecordedDisclosuresSpanish",
    abbreviation: "Spa",
    locale: "es-mx",
};

function getLanguageState(lang?: string | null): LanguageStoreState {
    switch (lang) {
        case "Spanish":
        case "es-US":
        case "PreRecordedDisclosuresSpanish":
        case "Spa":
        case "es-mx":
            return SPANISH;
        case "English":
        case "en-US":
        case "PreRecordedDisclosures":
        case "Eng":
        case "en-us":
        default:
            return ENGLISH;
    }
}

export const useLanguageStore = create<LanguageStoreProps>()(
    persist(
        devtools(
            (set) => ({
                ...ENGLISH,
                updateLanguage(lang?: string | null) {
                    set(getLanguageState(lang), false, "updateLanguage");
                },
            }),
            {
                enabled: !getIsProd(),
                store: "language-store",
                name: "workspace/sales-tools",
            },
        ),
        { name: "language-store" },
    ),
);

/**
 * Reset the language store on session end (host calls this, e.g. on sdkEventBus "session_changed"
 * with action "end"). Clears the persisted value AND resets the in-memory state to the English
 * default — resetting in-memory too prevents the previous session's language from leaking into
 * consumers (product info, coverage) for the frame(s) before the next session re-seeds it.
 */
export function clearLanguageStoreStorage() {
    useLanguageStore.persist.clearStorage();
    useLanguageStore.getState().updateLanguage(); // reset in-memory to English default
}
